import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { useEffect } from 'react';
import { Bold, Italic, List, ListOrdered, Undo, Redo } from 'lucide-react';

function ToolbarButton({
  onClick,
  active,
  disabled,
  children,
  title,
}: {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  title: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`rounded p-1 transition-colors ${
        active
          ? 'bg-[var(--vscode-primary)] text-white'
          : 'text-[var(--vscode-text-tertiary)] hover:bg-[var(--vscode-bg-hover)] hover:text-[var(--vscode-text-primary)]'
      } disabled:cursor-not-allowed disabled:opacity-40`}
    >
      {children}
    </button>
  );
}

function EditorToolbar({ editor }: { editor: Editor | null }) {
  if (!editor) return null;
  return (
    <div
      className="flex flex-wrap items-center gap-0.5 border-b px-2 py-1.5"
      style={{
        borderColor: 'var(--vscode-border-primary)',
        background: 'var(--vscode-bg-secondary)',
      }}
    >
      <ToolbarButton
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title="Undo"
      >
        <Undo className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title="Redo"
      >
        <Redo className="h-3.5 w-3.5" />
      </ToolbarButton>
      <div className="mx-1 h-4 w-px" style={{ background: 'var(--vscode-border-primary)' }} />
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        active={editor.isActive('bold')}
        title="Bold"
      >
        <Bold className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        active={editor.isActive('italic')}
        title="Italic"
      >
        <Italic className="h-3.5 w-3.5" />
      </ToolbarButton>
      <div className="mx-1 h-4 w-px" style={{ background: 'var(--vscode-border-primary)' }} />
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        active={editor.isActive('bulletList')}
        title="Bullet list"
      >
        <List className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        active={editor.isActive('orderedList')}
        title="Numbered list"
      >
        <ListOrdered className="h-3.5 w-3.5" />
      </ToolbarButton>
    </div>
  );
}

export interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  className?: string;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  minHeight = 120,
  className = '',
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          // visually style links inside the editor canvas
          class: 'rte-link',
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      Placeholder.configure({
        placeholder: placeholder ?? 'Type your message here…',
      }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        // prose-style editor content — light bg, readable dark text, links visible
        class: [
          'focus:outline-none px-3 py-3 text-sm leading-relaxed',
          // paragraphs
          '[&_p]:mb-2.5 [&_p:last-child]:mb-0',
          // strong / em
          '[&_strong]:font-semibold',
          '[&_em]:italic',
          // lists
          '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2',
          '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2',
          '[&_li]:my-0.5',
          // links — make them visibly blue and underlined regardless of any inline style
          '[&_.rte-link]:!text-[#2563EB] [&_.rte-link]:!underline [&_.rte-link]:cursor-pointer',
          '[&_a]:!text-[#2563EB] [&_a]:!underline [&_a]:cursor-pointer',
          // tables from email templates — give them some breathing room
          '[&_table]:border-collapse [&_table]:text-[13px] [&_table]:my-2',
          '[&_td]:align-top [&_td]:py-0.5',
        ].join(' '),
      },
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Sync when value changes externally (template switch or AI rewrite)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (current !== value) {
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  return (
    <div
      className={`overflow-hidden rounded-lg border ${className}`}
      style={{
        borderColor: 'var(--vscode-border-primary)',
        background: 'var(--vscode-bg-primary)',
        color: 'var(--vscode-text-primary)',
        fontFamily:
          'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        fontSize: '14px',
      }}
    >
      <EditorToolbar editor={editor} />
      <div style={{ minHeight }}>
        <EditorContent
          editor={editor}
          style={{ color: 'var(--vscode-text-primary)' }}
        />
      </div>
    </div>
  );
}
